import type {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';
import { MUTATING_HTTP_METHODS } from '@/js/lib/http/constants';

type RequestConfigWithCsrfRetry = InternalAxiosRequestConfig & {
  _csrfRetried?: boolean
};

export function readCsrfToken(): string | null {
  const content
    = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
      ?.content ?? '';

  return content.length > 0 ? content : null;
}

export function writeCsrfToken(token: string): void {
  const meta = document.querySelector<HTMLMetaElement>(
    'meta[name="csrf-token"]',
  );

  if (meta != null) {
    meta.content = token;
  }
}

function setCsrfHeader(
  config: InternalAxiosRequestConfig,
  token: string,
): void {
  if (typeof config.headers.set === 'function') {
    config.headers.set('X-CSRF-TOKEN', token);
    return;
  }

  config.headers['X-CSRF-TOKEN'] = token;
}

export function attachCsrfInterceptor(client: AxiosInstance): void {
  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const method = config.method?.toLowerCase();

    if (method != null && MUTATING_HTTP_METHODS.has(method)) {
      const token = readCsrfToken();

      if (token != null) {
        setCsrfHeader(config, token);
      }
      else if (import.meta.env.DEV) {
        console.error(
          '[http] Missing CSRF token for',
          method.toUpperCase(),
          config.url,
        );
      }
    }

    return config;
  });
}

export function attachCsrfRetryInterceptor(
  client: AxiosInstance,
  refreshToken: () => Promise<string>,
): void {
  let inflight: Promise<string> | null = null;

  client.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
      const config = error.config as RequestConfigWithCsrfRetry | undefined;

      if (
        error.response?.status !== 419
        || config == null
        || config._csrfRetried === true
      ) {
        return Promise.reject(error);
      }

      config._csrfRetried = true;

      inflight ??= refreshToken().finally(() => {
        inflight = null;
      });
      await inflight;

      return client.request(config);
    },
  );
}
